import Vue from "vue";
import { Component, Prop, Watch } from "vue-property-decorator";
import Multiselect from "vue-multiselect";
import Datepicker from "vuejs-datepicker";
import { validations, validationMessages } from "./validations";
import { Validation } from "vuelidate/vuelidate";
import { AzureBlobService } from "@/services/AzureBlobService";
import { NotificationService } from "@/services";
import uuid from "uuid";
import * as icons from "@/components/icons";

import {
    IBrand,
    IRequest,
    IAffiliate,
    ICountry,
    IMedia,
    Request,
    IUser,
    Role,
    RequestStatus,
    IRequestStatus,
    IFileMetadata,
    IFileKeys,
    IRequestFile,
    RequestFileTypes,
    Priority,
    ITypeOfCampaign,
    ITypeOfAssets,
} from "@/models";
import { PeoplePicker, TextEditor } from "@/components";

@Component({
    name: "edit-request",
    components: {
        Multiselect,
        Datepicker,
        "AdviceIcon": icons.AdviceIcon,
        "FormalComplianceIcon": icons.FormalComplianceIcon,
        "download-icon": icons.DownloadIcon,
        "delete-icon": icons.DeleteIcon,
        "close-icon": icons.CloseIcon,
        "people-picker": PeoplePicker,
        "text-editor": TextEditor,

    },
    validations,
    data() {
        return {
            quillEditorOptions: {
            formats: ["bold", "italic", "underline", "link", "list"],
            modules: {
                toolbar: ["bold", "italic", "underline", "link", { list: "bullet" }, { list: "ordered" }],
            },
            theme: "snow",
        },
        };
    },
})
export default class EditRequest extends Vue {
    @Prop({ type: Number })
    public requestId: number;

    public azureBlobService: AzureBlobService;
    public promises: Array<Promise<any>>;
    public dragging: boolean;
    public loading: boolean;
    public buttonsEnabled: boolean;
    public defaultRequest: IRequest;
    public fileToDelete: IRequestFile;

    private loaded: boolean;
    private previousRequestStatus: IRequestStatus;
    private extensionRegexp = new RegExp("(?:\.([^.]+))?$");

    constructor() {
        super();

        this.azureBlobService = null;
        this.promises = [];
        this.dragging = false;
        this.loading = false;
        this.buttonsEnabled = true;
        this.defaultRequest = new Request();
        this.loaded = false;
        this.fileToDelete = null;

        if (!this.$store.state.app.blobSettings) {
            this.promises.push(this.$store.dispatch("app/loadStorageConfig"));
        }

        if (!this.$store.state.app.user) {
            this.promises.push(this.$store.dispatch("app/getUser"));
        }

        if (!this.$store.state.request.isReferenceDataLoaded) {
            this.promises.push(this.$store.dispatch("request/fetchReferenceData"));
        }
    }

    public get uploadProgress(): number {
        const uploaded = this.azureBlobService.uploadProgress.reduce((sum, current) => sum + current.uploadProgress, 0);
        const totalSize = this.azureBlobService.uploadProgress.reduce((sum, current) => sum + current.fileSize, 0);

        return uploaded && totalSize ? uploaded / totalSize * 100 : 0;
    }

    public get RequestFileTypes(): typeof RequestFileTypes {
        return RequestFileTypes;
    }

    public get Priority(): typeof Priority {
        return Priority;
    }

    public get Priorities(): Array<string> {
        // Take the array of string related to index, ex: 1 will return Critical
        return Object.keys(Priority).filter(k => Number.isNaN(+k));
    }

    public get brands(): IBrand[] {
        return this.$store.getters["request/getBrands"];
    }

    public get affiliates(): IAffiliate[] {
        return this.$store.getters["request/getAffiliates"];
    }

    public get countries(): ICountry[] {
        return this.$store.getters["request/getCountries"];
    }

    public get typesOfCampaign(): ITypeOfCampaign[] {
        return this.$store.getters["request/getTypesOfCampaign"];
    }

    public get typesOfAssets(): ITypeOfAssets[] {
        return this.$store.getters["request/getTypesOfAssets"];
    }

    public get media(): IMedia[] {
        return this.$store.state.request.media;
    }

    public get sortedMedia(): IMedia[] {
        return this.$store.getters["request/getMedia"];
    }

    public get requestStatus(): IRequestStatus[] {
        return this.$store.state.request.requestStatus;
    }

    public get user(): IUser {
        return this.$store.state.app.user;
    }

    public get isAdmin(): boolean {
        return this.user?.role === Role.Admin;
    }

    public get isVip(): boolean {
        return this.user?.role === Role.Vip;
    }

    public get isUser(): boolean {
        return this.user?.role === Role.User;
    }

    public get isNewRequest(): boolean {
        return this.request?.requestId === 0 || this.request?.requestStatus?.name === RequestStatus.draft;
    }

    public get isTerminated(): boolean {
        return this.request?.requestStatus.step > 1;
    }

    public get isCanceled(): boolean {
        return this.request?.requestStatus?.name === RequestStatus.canceled.toString();
    }

    public get isReadonly(): boolean {
        return !(this.loaded && !this.isVip &&
            (this.isAdmin ||
                this.request.requestStatus.name === RequestStatus.toModify.toString() ||
                (this.request?.requestStatus?.step < 1 ?? false)));
    }

    public get canBeSaveAsDraft(): boolean {
        return !this.isReadonly && !this.isVip && (this.request?.requestStatus?.step < 1 ?? false);
    }

    public get canBeSubmitForReview(): boolean {
        return !this.isReadonly &&
            (this.isNewRequest ||
                this.request?.requestStatus?.step === 0 ||
                this.request.requestStatus.name === RequestStatus.toModify.toString());
    }

    public get canBeSubmit(): boolean {
        return !this.isReadonly &&
            this.request?.requestStatus?.step >= 1 &&
            this.request.requestStatus.name !== RequestStatus.toModify.toString();
    }

    public get finalRequestStatus(): IRequestStatus[] {
        return this.requestStatus?.filter(
            s => s.step > 1 && s.name !== RequestStatus.assessed.toString()
                 && s.name !== RequestStatus.canceled.toString()) ?? [];
    }

    public get pageTitle(): string {
        let title = "";
        if (!this.isReadonly) {
            title = title.concat(this.isNewRequest ? "new " : "edit the ");
        }

        if (this.isNewRequest) {
            title = this.isAdvice ? `${title}ask for advice` : `${title}formal compliance submission`;
        } else {
            title = this.request?.advice ? `${title}ask for advice` : `${title}formal compliance submission`;
        }

        return title.charAt(0).toUpperCase() + title.substring(1);
    }

    public get pageCssType(): string {
        if (this.isNewRequest) {
            return this.isAdvice ? "advice-request" : "formal-request";
        } else {
            return this.request?.advice ? "advice-request" : "formal-request";
        }
    }

    public get titleIcon(): string {
        if (this.isNewRequest) {
            return this.isAdvice ? "AdviceIcon" : "FormalComplianceIcon";
        } else {
            return this.request?.advice ? "AdviceIcon" : "FormalComplianceIcon";
        }
    }

    public get request(): IRequest {
        this.defaultRequest.advice = this.defaultRequest.advice || this.isAdvice;
        if (this.defaultRequest.requestId === 0 && this.requestId) {
            const req: IRequest = this.$store.state.request.singleRequest;
            this.defaultRequest = Request.fromRequest(req, this.defaultRequest);
            this.$store.dispatch("request/fetchUploadFiles", this.defaultRequest.key);
        }

        return this.defaultRequest;
    }

    public get validationMessages(): { [key: string]: string } {
        return validationMessages;
    }

    public get requestFiles(): IRequestFile[] {
        return this.updateUnsavedFiles(RequestFileTypes.Default);
    }

    public get coverFiles(): IRequestFile[] {
        return this.updateUnsavedFiles(RequestFileTypes.Cover);
    }

    private get isAdvice(): boolean {
        return this.$route.query.advice ? this.$route.query.advice === "true" : false;
    }

    @Watch("isAdvice", { deep: true })
    public onAdviceChanged(): void {
        this.defaultRequest = new Request();
        this.request.advice = this.isAdvice;
    }

    // Hook.
    public async created(): Promise<void> {
        if (this.requestId === null || this.requestId === undefined || !isNaN(this.requestId)) {
            if (!isNaN(this.requestId)) {
                this.promises.push(this.$store.dispatch("request/fetchSingleRequest", this.requestId));
            } else {
                this.defaultRequest = new Request();
            }

            await Promise.all(this.promises);

            this.azureBlobService = new AzureBlobService(
                this.$store.state.app.blobSettings.containerName,
                this.$store.state.app.blobSettings.blobUri,
                this.$store.state.app.blobSettings.sasToken);
        } else {
            this.$router.push({ name: "not-found" });
        }

        this.loaded = true;
    }

    public beforeRouteLeave(to, from, next) {
        this.$store.dispatch("request/clearAllSingleRequestData");
        this.$store.dispatch("app/clearStorageConfig");
        next();
    }

    public async saveRequestAsDraft(): Promise<void> {
        this.previousRequestStatus = this.request.requestStatus;
        this.request.requestStatus = this.requestStatus.find(s => s.name === RequestStatus.draft);
        if (this.validateBeforeSubmit(this.$v.request)) {
            await this.submitRequest();
        } else {
            this.resetRequestStatus();
        }
    }

    public async showSubmitForReview(): Promise<void> {
        this.previousRequestStatus = this.request.requestStatus;
        if (this.request.requestStatus.step < 1 ||
            this.request.requestStatus.name === RequestStatus.toModify.toString()) {
                this.request.requestStatus = this.requestStatus.find(s => s.name === RequestStatus.submitted);
        }

        if (this.validateBeforeSubmit(this.$v.request)) {
            this.$modal.show("submit-for-review");
        } else {
            this.resetRequestStatus();
        }
    }

    public async resetRequestStatus(): Promise<void> {
        this.request.requestStatus = this.previousRequestStatus;
        this.previousRequestStatus = null;
    }

    public async createOrUpdateRequest(): Promise<void> {
        if (this.validateBeforeSubmit(this.$v.request)) {
            await this.submitRequest();
        }
    }

    public async deletePhoto(photo: IRequestFile): Promise<void> {
        this.$modal.hide("deleteFile");
        await this.$store.dispatch("request/deleteFiles", photo);
        this.request.files = this.request.files.filter(f => f.fileKey !== photo.fileKey);
    }

    public onDragEnter(event: DragEvent): void {
        this.dragging = true;
    }

    public onDragLeave(event: DragEvent): void {
        this.dragging = false;
    }

    public async onDropFiles(event: DragEvent, fileType: RequestFileTypes): Promise<void> {
        this.dragging = false;
        await this.uploadFiles(event.dataTransfer.files, fileType);
    }

    public async handleFiles(event, fileType: RequestFileTypes): Promise<void> {
        await this.uploadFiles(event.target.files, fileType);
    }

    public setFileToDelete(event: any) {
        this.fileToDelete = event.params.file;
    }

    public isChecked(mediaId: number): boolean {
        return this.request?.media.some(m => m.mediaId === mediaId) ?? false;
    }

    public validateBeforeSubmit(rules: Validation): boolean {
        rules.$touch();
        return !rules.$invalid;
    }

    public getImage(file: IRequestFile): string {
        const extension: string = this.extensionRegexp.exec(file.fileName)[1];
        return this.isImage(extension) ? file.uri : "";
    }

    public getImageClass(file: IRequestFile): string {
        const extension: string = this.extensionRegexp.exec(file.fileName)[1];
        if (this.isImage(extension) === true) {
            return "image";
        } else {
            return this.isExtensionType(extension) + " unknown-file-type";
        }
    }

    public getImageIsRegistered(file: IRequestFile): string {
        const isFileRegistered: boolean = file.isRegistered;
        if (!isFileRegistered) {
            return "not-registered";
        }
        return "";
    }

    public getFileName(file: IRequestFile): string {
        return file?.fileName;
    }

    public updateUnsavedFiles(fileType: RequestFileTypes) {
        const files: IRequestFile[] = this.$store.state.request.uploadFiles || [];

        // For unsavedFiles, we need to retreive their blob info : extension, url, etc...
        // And we attach their type from the request.files from front
        files.forEach(file => {
            if (!file.isRegistered) {
                const tempFile: IRequestFile = this.request?.files?.filter(f => f.fileKey === file.fileKey)[0];
                if (tempFile) {
                    file.fileType = tempFile.fileType;
                }
            }
        });

        return files.filter(file => file.fileType === fileType);
    }

    private async uploadFiles(files: FileList, fileType: RequestFileTypes): Promise<void> {
        this.loading = true;
        const filePromises = [];

        // tslint:disable-next-line: prefer-for-of
        for (let i = 0; i < files.length; i++) {
            const fileExtension = this.extensionRegexp.exec(files[i]?.name)[1];
            let hasError = false;

            if (fileType === RequestFileTypes.Cover) {
                if (!this.isCoverImageExtensionAuthorized(fileExtension)) {
                    NotificationService.showError("You cannot upload file other than .JPG, .JPEG or .PNG");
                    hasError = true;
                } else {
                    if (files[i].size > 2e6) {
                        NotificationService.showError("Upload Size limited to 2Mb");
                        hasError = true;
                    }
                }
            } else {
                if (!this.isFileExtensionAuthorized(fileExtension)) {
                    NotificationService.showError("You cannot upload file other than .PDF or .DOCX or .XLSX for documents, .MP4 or .MP3 for videos, .JPG or .JPEG or .PNG for images");
                    hasError = true;
                }
            }

            if (!hasError) {
                filePromises.push(this.uploadFile(files[i], fileType));
            }
        }

        const keys: string[] = await Promise.all(filePromises);
        const keyArray: IRequestFile[] = keys
            .filter(k => !!k)
            .map(k => ({
                uri: "",
                requestKey: this.request.key,
                fileKey: k,
                fileName: "",
                isRegistered: false,
                creatorEmail: this.user.email,
                streamingLocator: "",
                streamingUrls: [],
                fileType,
            }));

        this.request.files = this.request.files.concat(keyArray);

        this.$store.dispatch("request/fetchUploadFiles", this.request.key);
        this.loading = false;
    }

    private async uploadFile(file: File, fileType: RequestFileTypes): Promise<string> {
        const metadata: IFileMetadata = {
            fileName: file.name,
            userEmail: this.user.email,
        };


        const uniqueName = uuid();

        if (this.requestId > 0) {
            const fileKeys: IFileKeys = {
                requestKey: this.request.key,
                fileKeys: [uniqueName],
                fileTypeIds: [fileType],
            };

            await this.$store.dispatch("request/registerFiles", fileKeys);
        }

        return await this.azureBlobService.uploadFile(file, this.request.key, uniqueName, metadata);
    }

    private async submitRequest(): Promise<void> {
        this.buttonsEnabled = false;
        const requestId: number = await this.$store.dispatch("request/createOrUpdateUserRequest", this.request);
        if (!!requestId) {
            this.$router.push("/requests/" + requestId);
        }

        this.buttonsEnabled = true;
    }

    private isImage(extension: string): boolean {
        switch (extension?.toLowerCase()) {
            case "jpg":
            case "jpeg":
            case "gif":
            case "bmp":
            case "png": {
                return true;
            }
            default:
                return false;
        }
    }

    private isCoverImageExtensionAuthorized(extension: string): boolean {
        const authorizedExtensions: string[] = [
            "jpg",
            "jpeg",
            "png",
        ];

        return authorizedExtensions.includes(extension?.toLocaleLowerCase());
    }

    private isFileExtensionAuthorized(extension: string): boolean {
        const authorizedExtensions: string[] = [
            "pdf",
            "mp4",
            "mp3",
            "doc",
            "xls",
            "jpg",
            "jpeg",
            "png",
        ];

        return authorizedExtensions.includes(extension?.toLocaleLowerCase());
    }

    private isExtensionType(extension: string): string {
        switch (extension?.toLowerCase()) {
            case "pdf": {
                return "pdf";
            }
            case "mpeg":
            case "avi":
            case "mp4":
            case "m4v":
            case "mkv": {
                return "mp4";
            }
            case "mp3": {
                return "mp3";
            }
            case "doc":
            case "docx": {
                return "doc";
            }
            case "xls":
            case "xlsx": {
                return "xls";
            }
            case "ppt":
            case "pptx": {
                return "ppt";
            }
            default:
                return "";
        }
    }
}
